import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { api } from "@/lib/api";
import { tint } from "@/lib/yuoDesign";
import { useAuth } from "@/context/AuthContext";
import { useResponsive } from "@/lib/responsive";

/**
 * Three-tier subscription page. Fully bilingual — the language pill in
 * the header toggles all copy between EN and RU without a reload.
 *
 * Uses backend price IDs (env: STRIPE_PRICE_*_MONTHLY/YEARLY) via
 * `POST /api/billing/checkout` with {plan, period, origin_url}. Same
 * endpoint that already powers subscriptions in production.
 */

const COPY = {
  en: {
    title: "Choose your YUO",
    subtitle: "One inner space. Three depths of it.",
    monthly: "Monthly",
    yearly: "Yearly",
    yearHint: "2 months free",
    perMonth: "/month",
    perYear: "/year",
    yearBilled: "billed annually",
    back: "back",
    lang: "EN",
    cta: {
      free: "Current plan",
      freeIdle: "Free",
      pro: "Choose Pro",
      master: "Choose Master",
      active: "Your plan",
      toYearly: "Switch to yearly",
      toMonthly: "Switch to monthly",
      switched: "Done — your plan is now",
    },
    plans: {
      free: {
        name: "YUO Free",
        subtitle: "Try the space for real.",
      },
      pro: {
        name: "YUO Pro",
        subtitle: "For daily practice.",
      },
      master: {
        name: "YUO Master",
        subtitle: "For deep, professional work.",
      },
    },
    features: [
      { key: "mirror_chat",    label: "Mirror chat",             values: ["3 daily", "10 daily", "20 daily"] },
      { key: "reflections",    label: "Diary reflections",       values: ["1 daily", "5 daily", "10 daily"] },
      { key: "diary",          label: "Diary notes",             values: ["Unlimited", "Unlimited", "Unlimited"] },
      { key: "book_breakdown", label: "Book / poetry analysis",  values: ["1 daily", "3 daily", "5 daily"] },
      { key: "content_chat",   label: "Creator dialogues",       values: ["2 daily", "5 daily", "10 daily"] },
      { key: "screenwriter",   label: "Screenwriter scripts",    values: ["1 / month", "5 / month", "15 / month"] },
      { key: "planning",       label: "Planning events",         values: ["5 / month", "15 / month", "25 / month"] },
      { key: "third_solution", label: "Third Solution (Symbiosis)", values: ["Not available", "6 cabinets / month (12 responses)", "12 cabinets / month (24 responses)"] },
      { key: "studio",         label: "Creator Studio (recorder)", values: ["Not available", "Not available", "Included"] },
      { key: "personalization",label: "Personalization",         values: ["—", "Enabled", "Enabled + future updates"] },
      { key: "future",         label: "All future new tools",    values: ["—", "—", "Included"] },
    ],
  },
  ru: {
    title: "Выберите свой YUO",
    subtitle: "Одно внутреннее пространство. Три глубины.",
    monthly: "Ежемесячно",
    yearly: "Годовой",
    yearHint: "≈ 2 месяца в подарок",
    perMonth: "/мес",
    perYear: "/год",
    yearBilled: "оплата раз в год",
    back: "назад",
    lang: "RU",
    cta: {
      free: "Текущий план",
      freeIdle: "Бесплатно",
      toYearly: "Перейти на годовую",
      toMonthly: "Перейти на месячную",
      switched: "Готово — ваш план теперь",
      pro: "Выбрать Pro",
      master: "Выбрать Master",
      active: "Ваш план",
    },
    plans: {
      free: {
        name: "YUO Free",
        subtitle: "Попробовать пространство по-настоящему.",
      },
      pro: {
        name: "YUO Pro",
        subtitle: "Для ежедневной практики.",
      },
      master: {
        name: "YUO Master",
        subtitle: "Для глубокой профессиональной работы.",
      },
    },
    features: [
      { key: "mirror_chat",    label: "Зеркало (чат)",           values: ["3 диалога / день", "10 диалогов / день", "25 больших диалогов / день"] },
      { key: "diary",          label: "Дневник (записи)",        values: ["Без лимита", "Без лимита", "Без лимита"] },
      { key: "reflections",    label: "Ответы дневника",         values: ["1", "5", "10 профессиональных"] },
      { key: "library",        label: "Библиотека",              values: ["Все бесплатные тексты", "+ дополнительные книги", "+ доп. книги + заказы книг"] },
      { key: "book_breakdown", label: "Разбор книг / стихов",    values: ["1 / день", "3 / день (Haiku)", "5 / день (Sonnet, профи)"] },
      { key: "content_chat",   label: "Creator (диалоги)",       values: ["2 / день", "5 больших / день", "10 / день"] },
      { key: "screenwriter",   label: "Сценарии",                values: ["1", "5", "15 + будущие"] },
      { key: "planning",       label: "События планировщика",    values: ["5", "15", "25 + будущие"] },
      { key: "personalization",label: "Персонализация",          values: ["—", "Включена", "Включена + будущие апдейты"] },
      { key: "symbiosis",      label: "Симбиоз (облака)",        values: ["—", "2", "6"] },
      { key: "third_solution", label: "Третье Решение",          values: ["—", "6 диалогов", "18 + будущие"] },
      { key: "model",          label: "AI-модель",               values: ["Claude Haiku", "Claude Haiku", "Claude Sonnet (разбор) · Haiku (чат)"] },
      { key: "future",         label: "Все будущие инструменты", values: ["—", "—", "Включены"] },
    ],
  },
};

const ACCENTS = {
  free: COLORS.textMuted,
  pro: COLORS.mirror,
  master: COLORS.library,
};

const PRICES = { pro: 12, master: 24 };
const YEAR_PRICES = { pro: 120, master: 240 };

export default function Paywall() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preferredPlan = (params.get("plan") || "").toLowerCase();
  const featureHint = params.get("feature") || "";
  const { user, refreshUser } = useAuth();

  const [lang, setLang] = useState("en");
  const [period, setPeriod] = useState("monthly");
  const { isPhone } = useResponsive();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const c = COPY[lang];
  const currentPlan = (user?.plan || "free").toLowerCase();
  const currentPeriod = (user?.plan_period || "monthly").toLowerCase();
  const isCurrent = (plan) => currentPlan === plan && (plan === "free" || currentPeriod === period);
  const [switched, setSwitched] = useState(null);

  useEffect(() => { refreshUser?.(); }, [refreshUser]);

  const checkout = async (plan) => {
    setBusy(plan);
    setError(null);
    try {
      if (currentPlan !== "free") {
        // Existing subscriber → change the subscription in place (one subscription, prorated)
        const ch = await api.billingChange(plan, period);
        if (ch?.ok) {
          await refreshUser?.();
          setSwitched(ch);
          setBusy(null);
          return;
        }
      }
      const originUrl = window.location.origin;
      const r = await api.createCheckout(originUrl, plan, period);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      const msg = e?.detail?.[lang === "ru" ? "message_ru" : "message_en"]
        || e?.message
        || "Could not open Stripe";
      setError(msg);
      setBusy(null);
    }
  };

  const priceFor = (plan) => (period === "yearly" ? YEAR_PRICES[plan] : PRICES[plan]);
  const suffix = period === "yearly" ? c.perYear : c.perMonth;

  const featureHintText = useMemo(() => {
    if (!featureHint) return null;
    const featureRow = c.features.find((f) => f.key === featureHint);
    if (!featureRow) return null;
    return lang === "ru"
      ? `Limit reached: ${featureRow.label}`
      : `Limit reached: ${featureRow.label}`;
  }, [featureHint, c, lang]);

  if (isPhone) {
    return (
      <PaywallPhone c={c} period={period} setPeriod={setPeriod} currentPlan={currentPlan} isCurrent={isCurrent}
        checkout={checkout} busy={busy} error={error} switched={switched} featureHintText={featureHintText}
        onBack={() => navigate(-1)} />
    );
  }

  return (
    <div
      data-testid="paywall-screen"
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        padding: "24px 20px 60px",
        color: COLORS.textPrimary,
        fontFamily: FONTS.body,
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <button
            onClick={() => navigate(-1)}
            data-testid="paywall-back"
            style={{
              display: "inline-flex", alignItems: "center", gap: 12,
              background: "transparent", border: "none", padding: 0, cursor: "pointer",
              color: "var(--fg-55)",
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: "50%",
              background: "radial-gradient(34% 30% at 32% 26%, #fff, #8AFFC4 22%, #05060c 100%)",
              boxShadow: "0 0 12px rgba(138,255,196,0.8)",
            }} />
            <span>{c.back}</span>
          </button>
          <div style={{ display: "flex", gap: 6 }}>
            {["en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`paywall-lang-${l}`}
                style={{
                  padding: "4px 12px",
                  borderRadius: 999,
                  border: `1px solid ${lang === l ? COLORS.library : COLORS.border}`,
                  background: lang === l ? COLORS.library : "transparent",
                  color: lang === l ? "#000" : COLORS.textSecondary,
                  fontSize: 11,
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {featureHintText && (
          <div
            data-testid="paywall-feature-hint"
            style={{
              padding: "10px 16px",
              borderRadius: 999,
              border: `1px solid rgba(var(--role-yellow-rgb),0.33)`,
              background: `rgba(var(--role-yellow-rgb),0.08)`,
              color: COLORS.library,
              fontSize: 13,
              display: "inline-block",
              marginBottom: 12,
            }}
          >
            {featureHintText}
          </div>
        )}

        {switched && (
          <div data-testid="paywall-switched" style={{
            padding: "12px 16px", borderRadius: 12, marginBottom: 14, fontSize: 13,
            border: `1px solid rgba(var(--role-green-rgb),0.33)`, background: `rgba(var(--role-green-rgb),0.07)`, color: COLORS.symbiosis,
          }}>
            {c.cta.switched} {switched.plan.toUpperCase()} · {switched.period}
            {switched.amount_paid != null ? ` · €${switched.amount_paid.toFixed(2)}` : ""}
            {switched.renews_at ? ` · next billing ${new Date(switched.renews_at).toLocaleDateString()}` : ""}
          </div>
        )}
        {error && (
          <div
            data-testid="paywall-error"
            style={{
              padding: "12px 16px",
              borderRadius: 12,
              border: `1px solid rgba(var(--role-coral-rgb),0.33)`,
              background: `rgba(var(--role-coral-rgb),0.07)`,
              color: COLORS.error,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <h1 style={{ fontFamily: FONTS.heading, fontSize: 40, letterSpacing: -1, margin: "8px 0 6px" }}>
          {c.title}
        </h1>
        <div style={{ color: COLORS.textMuted, fontSize: 14, marginBottom: 24 }}>{c.subtitle}</div>

        {/* Billing period toggle */}
        <div
          style={{
            display: "inline-flex",
            padding: 4,
            borderRadius: 999,
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
            marginBottom: 28,
          }}
          data-testid="paywall-period-toggle"
        >
          {["monthly", "yearly"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`paywall-period-${p}`}
              style={{
                padding: "8px 20px",
                borderRadius: 999,
                border: "none",
                background: period === p ? COLORS.library : "transparent",
                color: period === p ? "#000" : COLORS.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {p === "monthly" ? c.monthly : `${c.yearly} · ${c.yearHint}`}
            </button>
          ))}
        </div>

        {/* Three columns */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16 }}>
          {["free", "pro", "master"].map((plan) => {
            const accent = ACCENTS[plan];
            const focused = preferredPlan === plan;
            const p = c.plans[plan];
            return (
              <div
                key={plan}
                data-testid={`plan-column-${plan}`}
                style={{
                  border: `1px solid ${focused ? accent : COLORS.border}`,
                  background: focused
                    ? `linear-gradient(180deg, ${accent}22 0%, ${COLORS.surface} 55%)`
                    : COLORS.surface,
                  borderRadius: 22,
                  padding: 24,
                  boxShadow: focused
                    ? `0 0 0 1px ${accent}55, 0 20px 60px -20px ${accent}66`
                    : "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  minHeight: 460,
                }}
              >
                <div>
                  <div style={{ color: accent, fontFamily: FONTS.heading, fontSize: 12, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 6 }}>
                    {p.name}
                  </div>
                  <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 30, fontWeight: 700, lineHeight: 1 }}>
                    {plan === "free" ? (
                      lang === "ru" ? "0 €" : "€0"
                    ) : (
                      <>
                        {lang === "ru" ? `${priceFor(plan)} €` : `€${priceFor(plan)}`}
                        <span style={{ fontSize: 14, color: COLORS.textMuted, fontWeight: 400, marginLeft: 6 }}>{suffix}</span>
                      </>
                    )}
                  </div>
                  {plan !== "free" && (
                    <div data-testid={`price-alt-${plan}`} style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>
                      {period === "yearly"
                        ? `${c.yearBilled} · €${PRICES[plan]}${c.perMonth} monthly`
                        : `or €${YEAR_PRICES[plan]}${c.perYear} · ${c.yearHint}`}
                    </div>
                  )}
                  <div style={{ color: COLORS.textSecondary, fontSize: 13, marginTop: 10, minHeight: 34 }}>
                    {p.subtitle}
                  </div>
                </div>

                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                  {c.features.map((f) => {
                    const idx = ["free","pro","master"].indexOf(plan);
                    return (
                      <div key={f.key} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ color: COLORS.textMuted, fontSize: 12, flex: 1, lineHeight: "16px" }}>{f.label}</div>
                        <div style={{ color: COLORS.textPrimary, fontSize: 12, textAlign: "right", flex: 1, lineHeight: "16px" }}>
                          {f.values[idx]}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {plan === "free" ? (
                  <button
                    disabled
                    data-testid={`plan-cta-${plan}`}
                    style={{
                      padding: "12px 18px",
                      borderRadius: 999,
                      background: "transparent",
                      color: COLORS.textMuted,
                      border: `1px solid ${COLORS.border}`,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "not-allowed",
                    }}
                  >
                    {currentPlan === "free" ? c.cta.free : c.cta.freeIdle}
                  </button>
                ) : (
                  <button
                    onClick={() => checkout(plan)}
                    disabled={busy === plan || isCurrent(plan)}
                    data-testid={`plan-cta-${plan}`}
                    style={{
                      padding: "12px 18px",
                      borderRadius: 999,
                      background: isCurrent(plan) ? "transparent" : accent,
                      color: isCurrent(plan) ? accent : "#0D0D1A",
                      border: isCurrent(plan) ? `1px solid ${tint(accent, "55")}` : "none",
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: isCurrent(plan) ? "default" : "pointer",
                      opacity: busy === plan ? 0.6 : 1,
                    }}
                  >
                    {isCurrent(plan) ? c.cta.active :
                     busy === plan ? "…" :
                     currentPlan === plan ? `${period === "yearly" ? c.cta.toYearly : c.cta.toMonthly} · €${priceFor(plan)}` :
                     plan === "pro" ? c.cta.pro : c.cta.master}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---- Phone (≤600px) — "Subscription mobile" mockup ------------------------
const PHONE_PLANS = [
  { key: "free",   title: "YUO FREE",   c: "rgba(238,240,246,0.55)", rgb: "238,240,246", m: 0,  yr: 0 },
  { key: "pro",    title: "YUO PRO",    c: "#5b9bff", rgb: "91,155,255",  m: 12, yr: 120 },
  { key: "master", title: "YUO MASTER", c: "#e9c46a", rgb: "233,196,106", m: 24, yr: 240 },
];
const PHONE_MONO = "'JetBrains Mono', ui-monospace, monospace";
const LANG_CYCLE = { EN: "RU", RU: "PT", PT: "EN" };

function PaywallPhone({ c, period, setPeriod, currentPlan, isCurrent, checkout, busy, error, switched, featureHintText, onBack }) {
  const [langLabel, setLangLabel] = useState("EN");
  const yearly = period === "yearly";
  const go = (key) => document.querySelector(`[data-plan="${key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const segBtn = (on) => ({
    height: 40, border: "none", borderRadius: 999, cursor: "pointer", fontFamily: PHONE_MONO, fontSize: 11.5, fontWeight: 500,
    background: on ? "#e9c46a" : "transparent", color: on ? "#05060c" : "rgba(238,240,246,0.7)", transition: "all .3s", boxShadow: "none",
  });

  return (
    <div data-testid="paywall-screen" style={{
      minHeight: "100vh", background: "#05060c", color: "#eef0f6", fontFamily: PHONE_MONO, position: "relative",
    }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(90% 40% at 10% -5%, rgba(61,220,151,0.08), transparent 65%), radial-gradient(70% 30% at 100% 30%, rgba(74,140,255,0.08), transparent 70%)" }} />
      <div style={{ position: "relative", display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 4px" }}>
          <button type="button" data-testid="paywall-back" onClick={onBack}
            style={{ height: 38, display: "flex", alignItems: "center", gap: 10, padding: "0 14px 0 4px", border: "none", borderRadius: 999, background: "transparent", color: "rgba(238,240,246,0.7)", fontSize: 10, letterSpacing: "0.3em", cursor: "pointer", fontFamily: PHONE_MONO, boxShadow: "none" }}>
            <span style={{ width: 14, height: 14, borderRadius: "50%", background: "radial-gradient(38% 34% at 32% 26%, #fff 0%, #3ddc97 22%, #0d3a28 62%, #05060c 100%)", boxShadow: "0 0 12px rgba(61,220,151,0.5)" }} />
            BACK
          </button>
          <button type="button" data-testid="paywall-lang-cycle" onClick={() => setLangLabel((l) => LANG_CYCLE[l])}
            style={{ width: 44, height: 34, border: "none", borderRadius: 999, background: "#e9c46a", color: "#05060c", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.08em", boxShadow: "0 0 16px rgba(233,196,106,0.3)", cursor: "pointer", fontFamily: PHONE_MONO }}>
            {langLabel}
          </button>
        </div>

        <div style={{ padding: "18px 20px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          <h1 style={{ margin: 0, fontFamily: PHONE_MONO, fontSize: 30, fontWeight: 400, letterSpacing: "-0.01em" }}>{c.title}</h1>
          <p style={{ margin: 0, fontSize: 12, color: "rgba(238,240,246,0.45)" }}>{c.subtitle}</p>
        </div>

        {(featureHintText || switched || error) && (
          <div style={{ padding: "14px 20px 0", display: "flex", flexDirection: "column", gap: 8 }}>
            {featureHintText && <div data-testid="paywall-feature-hint" style={{ padding: "10px 16px", borderRadius: 999, boxShadow: "inset 0 0 0 1px rgba(233,196,106,0.33)", background: "rgba(233,196,106,0.08)", color: "#e9c46a", fontSize: 12 }}>{featureHintText}</div>}
            {switched && (
              <div data-testid="paywall-switched" style={{ padding: "12px 16px", borderRadius: 20, fontSize: 12, boxShadow: "inset 0 0 0 1px rgba(61,220,151,0.33)", background: "rgba(61,220,151,0.07)", color: "#5cf0a6" }}>
                {c.cta.switched} {switched.plan.toUpperCase()} · {switched.period}
                {switched.amount_paid != null ? ` · €${switched.amount_paid.toFixed(2)}` : ""}
                {switched.renews_at ? ` · next billing ${new Date(switched.renews_at).toLocaleDateString()}` : ""}
              </div>
            )}
            {error && <div data-testid="paywall-error" style={{ padding: "12px 16px", borderRadius: 20, fontSize: 12, boxShadow: "inset 0 0 0 1px rgba(255,106,85,0.33)", background: "rgba(255,106,85,0.07)", color: "#ff7a62" }}>{error}</div>}
          </div>
        )}

        <div style={{ padding: "20px 20px 0" }}>
          <div data-testid="paywall-period-toggle" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.6fr)", gap: 4, padding: 4, borderRadius: 999, background: "rgba(238,240,246,0.05)", boxShadow: "inset 0 0 0 1px rgba(110,130,200,0.18)" }}>
            <button type="button" data-testid="paywall-period-monthly" onClick={() => setPeriod("monthly")} style={segBtn(!yearly)}>Monthly</button>
            <button type="button" data-testid="paywall-period-yearly" onClick={() => setPeriod("yearly")} style={segBtn(yearly)}>Yearly · 2 months free</button>
          </div>
        </div>

        <div style={{ padding: "14px 20px 0", display: "flex", gap: 6 }}>
          {PHONE_PLANS.map((p) => (
            <button key={p.key} type="button" data-testid={`paywall-jump-${p.key}`} onClick={() => go(p.key)}
              style={{ flex: 1, height: 32, border: "none", borderRadius: 999, fontFamily: PHONE_MONO, cursor: "pointer", boxShadow: "none",
                background: currentPlan === p.key ? `rgba(${p.rgb},0.12)` : "rgba(238,240,246,0.04)", color: p.c,
                fontSize: 9.5, letterSpacing: "0.2em", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: p.c }} />{p.title.split(" ")[1]}
            </button>
          ))}
        </div>

        <div style={{ padding: "16px 16px 40px", display: "flex", flexDirection: "column", gap: 14 }}>
          {PHONE_PLANS.map((p, pi) => {
            const cur = currentPlan === p.key;
            const free = p.m === 0;
            const price = free ? "€0" : `€${yearly ? p.yr / 12 : p.m}`;
            const alt = free ? "" : (yearly ? `billed €${p.yr}/year · 2 months free` : `or €${p.yr}/year · 2 months free`);
            const switching = cur && !free && !isCurrent(p.key);
            let label, bg, fg, shadow, disabled = false;
            if (free) {
              label = cur ? c.cta.active : c.cta.freeIdle; disabled = true;
              bg = "transparent"; fg = cur ? p.c : "rgba(238,240,246,0.45)";
              shadow = cur ? `inset 0 0 0 1px rgba(${p.rgb},0.45)` : "inset 0 0 0 1px rgba(238,240,246,0.08)";
              if (!cur) bg = "rgba(238,240,246,0.04)";
            } else if (isCurrent(p.key)) {
              label = c.cta.active; disabled = true; bg = "transparent"; fg = p.c; shadow = `inset 0 0 0 1px rgba(${p.rgb},0.45)`;
            } else {
              label = busy === p.key ? "…" : switching ? `${yearly ? c.cta.toYearly : c.cta.toMonthly} · €${yearly ? p.yr : p.m}` : (p.key === "pro" ? c.cta.pro : c.cta.master);
              bg = p.c; fg = "#05060c"; shadow = `0 0 22px rgba(${p.rgb},0.35)`; disabled = busy === p.key;
            }
            return (
              <div key={p.key} data-plan={p.key} data-testid={`plan-card-${p.key}`} style={{
                flex: "none", borderRadius: 28, padding: "24px 20px 20px", display: "flex", flexDirection: "column", scrollMarginTop: 12,
                background: cur
                  ? `radial-gradient(120% 60% at 0% 0%, rgba(${p.rgb},0.10), transparent 60%), linear-gradient(180deg, rgba(238,240,246,0.045), rgba(238,240,246,0.018))`
                  : "linear-gradient(180deg, rgba(238,240,246,0.045), rgba(238,240,246,0.018))",
                boxShadow: cur ? `inset 0 0 0 1px rgba(${p.rgb},0.22)` : "inset 0 0 0 1px rgba(238,240,246,0.06)",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 10.5, letterSpacing: "0.28em", color: p.c }}>{p.title}</span>
                  {cur && <span data-testid={`plan-current-${p.key}`} style={{ height: 22, display: "flex", alignItems: "center", padding: "0 10px", borderRadius: 999, background: "rgba(233,196,106,0.12)", color: "#e9c46a", fontSize: 8.5, letterSpacing: "0.2em" }}>CURRENT</span>}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10 }}>
                  <span data-testid={`plan-price-${p.key}`} style={{ fontSize: 36, fontWeight: 600, letterSpacing: "-0.02em" }}>{price}</span>
                  {!free && <span style={{ fontSize: 13, color: "rgba(238,240,246,0.42)" }}>/month</span>}
                </div>
                <span style={{ minHeight: 15, marginTop: 4, fontSize: 10.5, color: "rgba(238,240,246,0.38)" }}>{alt}</span>
                <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "rgba(238,240,246,0.82)" }}>{c.plans[p.key].subtitle}</p>
                <div style={{ height: 1, margin: "18px 0 6px", background: "linear-gradient(90deg, rgba(238,240,246,0.12), rgba(238,240,246,0.03) 70%, transparent)" }} />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {c.features.map((f) => {
                    const v = f.values[pi];
                    const dim = v === "Not available" || v === "—";
                    return (
                      <div key={f.key} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12, padding: "8px 0", alignItems: "start" }}>
                        <span style={{ fontSize: 11, lineHeight: 1.45, color: "rgba(238,240,246,0.42)" }}>{f.label}</span>
                        <span style={{ fontSize: 11, lineHeight: 1.45, textAlign: "right", color: dim ? "rgba(238,240,246,0.32)" : "#eef0f6", overflowWrap: "anywhere" }}>{v}</span>
                      </div>
                    );
                  })}
                </div>
                <button type="button" data-testid={`plan-cta-${p.key}`} onClick={() => !disabled && checkout(p.key)} disabled={disabled}
                  style={{ marginTop: 18, height: 48, border: "none", borderRadius: 999, background: bg, boxShadow: shadow, color: fg, fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", cursor: disabled ? "default" : "pointer", transition: "all .3s", fontFamily: PHONE_MONO, opacity: 1 }}>
                  {label}
                </button>
              </div>
            );
          })}
          <p style={{ margin: "6px 4px 0", fontSize: 10, lineHeight: 1.6, color: "rgba(238,240,246,0.32)", textAlign: "center" }}>Cancel anytime. Prices include VAT.</p>
        </div>
      </div>
    </div>
  );
}
